import { myVariablesKeys } from '@/hooks/queries/variables/variables-v2';
import { useActiveTenant } from '@/hooks/useActiveTenant';
import PracticeService from '@/services/practice-service';
import VariableService from '@/services/variables';
import { useUserStore } from '@/store/use-user-store';
import type { MyVariable } from '@/types/variables';
import { getMyVariablesList } from '@/utils/profile-variables';
import {
  acknowledgeSoulMapReady,
  checkSoulMapReady,
  isSoulMapReadySeenVariable,
  SOUL_MAP_READY_SEEN_VARIABLE,
  type SoulMapPracticeSummary,
  type SoulMapReadyCheckResult,
  type SoulMapReadyItem,
} from '@/utils/soul-map-ready';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/** Re-check while the app is open, so a PDF finishing mid-session shows up. */
const RECHECK_INTERVAL_MS = 60_000;
/** Route changes re-check at most this often. */
const ROUTE_CHECK_THROTTLE_MS = 15_000;
/** Shares the cache with the chat page's useSoulMapDocuments. */
const DOCUMENT_STALE_MS = 30_000;
/** Creating a practice invalidates ['practices'], which refreshes this too. */
const PRACTICE_LIST_STALE_MS = 5 * 60_000;

export const soulMapReadyKey = (
  tenantId: number | null,
  userId: number | string | null | undefined
) => ['soul-map-ready', tenantId, userId] as const;

/**
 * App-wide check for Soul Map PDFs that finished since the user last saw one.
 * Feature-gated by the `soul_map_ready_seen` user variable existing.
 */
export function useSoulMapReadyNotice() {
  const queryClient = useQueryClient();
  const userId = useUserStore((state) => state.user?.id);
  const { tenantId } = useActiveTenant();
  const { pathname } = useLocation();
  const enabled = userId != null && tenantId != null;
  const sessionKey = `${tenantId}:${userId}`;
  const queryKey = soulMapReadyKey(tenantId, userId);

  const query = useQuery<SoulMapReadyCheckResult>({
    queryKey,
    queryFn: () =>
      checkSoulMapReady({
        getMyVariables: async () =>
          getMyVariablesList(
            await queryClient.fetchQuery({
              queryKey: myVariablesKeys.list({}),
              queryFn: () =>
                VariableService.getMyVariables(undefined, {
                  suppressErrorToast: true,
                }),
              staleTime: DOCUMENT_STALE_MS,
            })
          ),
        getPractices: () =>
          queryClient.fetchQuery({
            queryKey: ['practices', 'all-for-user', userId],
            queryFn: () =>
              PracticeService.getAllUserPractices<SoulMapPracticeSummary>(
                userId!
              ),
            staleTime: PRACTICE_LIST_STALE_MS,
          }),
        getSoulMapDocuments: (practiceId) =>
          queryClient.fetchQuery({
            queryKey: ['soul-map-documents', String(practiceId)],
            queryFn: () => PracticeService.getSoulMapDocuments(practiceId),
            staleTime: DOCUMENT_STALE_MS,
          }),
      }),
    enabled,
    retry: false,
    staleTime: ROUTE_CHECK_THROTTLE_MS,
    refetchOnWindowFocus: false,
    // Feature off: stop polling. Route changes still re-check the variable.
    refetchInterval: (q) =>
      q.state.data?.enabled ? RECHECK_INTERVAL_MS : false,
  });

  // Throttled re-check on route changes. The first render is the initial fetch.
  const { refetch } = query;
  const previousPathname = useRef(pathname);
  useEffect(() => {
    if (!enabled || previousPathname.current === pathname) return;
    previousPathname.current = pathname;
    const state = queryClient.getQueryState(queryKey);
    if (
      state?.fetchStatus !== 'fetching' &&
      Date.now() - (state?.dataUpdatedAt ?? 0) > ROUTE_CHECK_THROTTLE_MS
    ) {
      void refetch();
    }
    // queryKey is rebuilt every render; its parts are the real dependencies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, pathname, queryClient, refetch, tenantId, userId]);

  const acknowledge = useCallback(
    (items: SoulMapReadyItem[]) =>
      acknowledgeSoulMapReady(sessionKey, items, {
        patch: (watermark) =>
          VariableService.updateUserVariable(
            { [SOUL_MAP_READY_SEEN_VARIABLE]: watermark },
            { suppressErrorToast: true }
          ),
        applyLocally: (watermark) => {
          queryClient.setQueriesData<unknown>(
            { queryKey: myVariablesKeys.lists() },
            (data: unknown) => withWatermark(data, watermark)
          );
          queryClient.setQueryData<SoulMapReadyCheckResult>(
            soulMapReadyKey(tenantId, userId),
            (data: SoulMapReadyCheckResult | undefined) =>
              data?.enabled ? { ...data, watermark, items: [] } : data
          );
        },
      }),
    [queryClient, sessionKey, tenantId, userId]
  );

  return { data: query.data, sessionKey, acknowledge };
}

function withWatermark(data: unknown, watermark: string): unknown {
  const update = (variables: MyVariable[]) =>
    variables.map((variable) =>
      isSoulMapReadySeenVariable(variable)
        ? { ...variable, value: watermark }
        : variable
    );

  if (Array.isArray(data)) return update(data);
  if (data && typeof data === 'object' && 'results' in data) {
    return { ...data, results: update(getMyVariablesList(data)) };
  }
  return data;
}
